import { useState } from "react";
import type { Instrument, Operation, Position } from "../../sdk";
import { Button, CoinMark, Empty, RiskMeter, Segmented, Tabs } from "../../components/ui";
import { useAccount, useAccountState } from "../../state/account";
import { cancelIntent, closePositions } from "../../state/actions";
import { useInstruments, useMark } from "../../state/market";
import { useValuation, useValuations } from "../../state/valuation";
import { ago, until } from "../../lib/time";
import { big, fmt, fromRaw, paper, pctBp, pnl, price, tone, usd } from "../../lib/num";

type Tab = "positions" | "pending" | "closed";

function Side({ isLong, leverage }: { isLong: boolean; leverage: number }) {
  return (
    <span className={`side-tag ${isLong ? "up" : "down"}`}>
      {isLong ? "LONG" : "SHORT"} {leverage}×
    </span>
  );
}

function useClosing(): Set<string> {
  const operations = useAccount()?.operations ?? [];
  return new Set(operations.filter((op) => op.action === "closePositions").flatMap((op) => op.positionIds));
}

function PositionRow({ position, instrument, closing, onClose, busy }: { position: Position; instrument: Instrument | undefined; closing: boolean; onClose: () => void; busy: boolean }) {
  const valuation = useValuation(position.positionId);
  const mark = useMark(position.instrumentId);
  const scale = instrument?.priceScale ?? "1";
  const value = valuation ? big(valuation.payoutPnl) : null;
  const breakdown = valuation
    ? `Move ${pnl(valuation.rawPnl)} · impact and fee ${pnl(-big(valuation.haircut))} · paid ${pnl(valuation.payoutPnl)}`
    : undefined;
  return (
    <tr className={valuation?.liquidated ? "ps-row is-busting" : "ps-row"}>
      <td>
        <span className="side">
          <CoinMark symbol={instrument?.symbol ?? "?"} size={18} />
          {instrument?.symbol}
          <Side isLong={position.isLong} leverage={position.leverage} />
        </span>
      </td>
      <td>{usd(position.notional, { compact: 6 })}</td>
      <td>{usd(position.margin)}</td>
      <td>{price(position.entryPrice, scale)}</td>
      <td className="ps-col-mark">{mark ? price(mark.raw, scale) : "—"}</td>
      <td className="down">{price(position.bustPrice, scale)}</td>
      <td title={breakdown}>
        {value === null ? (
          "—"
        ) : (
          <span className={`ps-pnl ${tone(value)}`}>
            {pnl(value)}
            <span className="ps-roe">{pctBp(valuation?.roeBp ?? "0", { sign: true })}</span>
          </span>
        )}
      </td>
      <td>
        <RiskMeter bp={valuation?.riskBp ?? 0} />
      </td>
      <td className="muted ps-col-age">{ago(position.openedAtMs)}</td>
      <td>
        <Button size="sm" tone="secondary" onClick={onClose} busy={busy} disabled={closing}>
          {closing ? "Closing" : "Close"}
        </Button>
      </td>
    </tr>
  );
}

function PositionCard({ position, instrument, closing, onClose, busy }: { position: Position; instrument: Instrument | undefined; closing: boolean; onClose: () => void; busy: boolean }) {
  const valuation = useValuation(position.positionId);
  const mark = useMark(position.instrumentId);
  const scale = instrument?.priceScale ?? "1";
  const value = valuation ? big(valuation.payoutPnl) : null;
  return (
    <div className="ps-card">
      <div className="ps-card-head">
        <span className="side">
          <CoinMark symbol={instrument?.symbol ?? "?"} size={18} />
          {instrument?.symbol}
          <Side isLong={position.isLong} leverage={position.leverage} />
        </span>
        <span className={`num ps-pnl ${value === null ? "" : tone(value)}`}>
          {value === null ? "—" : pnl(value)}
          {valuation && <span className="ps-roe">{pctBp(valuation.roeBp, { sign: true })}</span>}
        </span>
      </div>
      <div className="ps-card-grid num">
        <span className="label">Size</span>
        <span>{usd(position.notional, { compact: 6 })}</span>
        <span className="label">Margin</span>
        <span>{usd(position.margin)}</span>
        <span className="label">Entry</span>
        <span>{price(position.entryPrice, scale)}</span>
        <span className="label">Mark</span>
        <span>{mark ? price(mark.raw, scale) : "—"}</span>
        <span className="label">Bust</span>
        <span className="down">{price(position.bustPrice, scale)}</span>
        <span className="label">Risk</span>
        <span>
          <RiskMeter bp={valuation?.riskBp ?? 0} />
        </span>
      </div>
      <Button size="sm" block onClick={onClose} busy={busy} disabled={closing}>
        {closing ? "Closing" : "Close position"}
      </Button>
    </div>
  );
}

function describeOp(op: Operation, instruments: Instrument[]): string {
  const symbol = instruments.find((instrument) => instrument.id === op.instrumentId)?.symbol ?? "";
  switch (op.action) {
    case "openPosition":
      return `Open ${op.isLong ? "long" : "short"} ${symbol} ${op.leverage}× · ${usd(op.amount ?? "0")}`;
    case "closePositions":
      return `Close ${op.positionIds.length} position${op.positionIds.length === 1 ? "" : "s"}`;
    case "withdrawToCore":
      return `Withdraw ${usd(op.amount ?? "0")}`;
    case "paperStake":
      return `Stake ${paper(op.amount ?? "0")} PAPER`;
    case "paperUnstake":
      return `Unstake ${paper(op.amount ?? "0")} PAPER`;
    case "paperClaim":
      return "Claim rewards";
    case "registerSessionKey":
      return "Enable one-click trading";
    default:
      return op.action;
  }
}

function Pending({ operations, instruments }: { operations: Operation[]; instruments: Instrument[] }) {
  const [busy, setBusy] = useState<string | null>(null);
  if (!operations.length) return <Empty title="Nothing pending" body="Intents the relayer has taken and not yet settled show here, for a block or two." />;
  return (
    <div className="ps-list">
      {operations.map((op) => (
        <div key={op.intentId} className="ps-op">
          <span className="spinner ps-op-spin" aria-hidden />
          <span className="ps-op-what">{describeOp(op, instruments)}</span>
          <span className="label">{ago(op.acceptedAtMs)} ago · expires in {until(op.deadline * 1000)}</span>
          {(op.action === "openPosition" || op.action === "closePositions") && (
            <Button
              size="sm"
              tone="ghost"
              busy={busy === op.intentId}
              onClick={async () => {
                setBusy(op.intentId);
                try {
                  await cancelIntent(op.intentId);
                } catch {
                  // told by a toast
                } finally {
                  setBusy(null);
                }
              }}
            >
              Cancel
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}

function Closed({ instruments }: { instruments: Instrument[] }) {
  const closed = useAccountState().closed;
  if (!closed.length) return <Empty title="No closes yet" body="Closes and liquidations since you opened this page land here with their final PnL." />;
  return (
    <div className="table-scroll">
      <table className="table">
        <thead>
          <tr>
            <th>Market</th>
            <th>Margin</th>
            <th>Result</th>
            <th>PnL</th>
            <th>PAPER minted</th>
            <th>When</th>
          </tr>
        </thead>
        <tbody>
          {closed.map((item) => (
            <tr key={`${item.positionId}-${item.at}`}>
              <td>
                <span className="side">
                  {instruments.find((instrument) => instrument.id === item.instrumentId)?.symbol}
                  <Side isLong={item.isLong} leverage={item.leverage} />
                </span>
              </td>
              <td>{usd(item.margin)}</td>
              <td className={item.liquidated ? "down" : "muted"}>{item.liquidated ? "Liquidated" : "Closed"}</td>
              <td className={tone(item.netPnl)}>{pnl(item.netPnl)}</td>
              <td>{big(item.paperMinted) > 0n ? fmt(fromRaw(item.paperMinted, 18), { places: 2 }) : "—"}</td>
              <td className="muted">{ago(item.at)} ago</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Positions({ instrument }: { instrument: Instrument }) {
  const account = useAccount();
  const valuations = useValuations();
  const instruments = useInstruments();
  const closing = useClosing();
  const [tab, setTab] = useState<Tab>("positions");
  const [scope, setScope] = useState<"all" | "market">("all");
  const [busy, setBusy] = useState<Set<string>>(new Set());
  const [confirmAll, setConfirmAll] = useState(false);

  const positions = (account?.positions ?? []).filter((position) => scope === "all" || position.instrumentId === instrument.id);
  const operations = account?.operations ?? [];
  const closedCount = useAccountState().closed.length;

  const close = async (ids: string[], label: string) => {
    setBusy((held) => new Set([...held, ...ids]));
    try {
      await closePositions(ids, label);
    } catch {
      // told by a toast
    } finally {
      setBusy((held) => new Set([...held].filter((id) => !ids.includes(id))));
    }
  };

  const openIds = positions.map((position) => position.positionId).filter((id) => !closing.has(id));
  const symbolOf = (position: Position) => instruments.find((item) => item.id === position.instrumentId);

  return (
    <section className="panel panel-flush ps">
      <header className="ps-head">
        <Tabs<Tab>
          label="Account"
          value={tab}
          onChange={setTab}
          tabs={[
            { value: "positions", label: <>Positions<span className="tab-count">{account?.positions.length ?? 0}</span></> },
            { value: "pending", label: <>Pending<span className="tab-count">{operations.length}</span></> },
            { value: "closed", label: <>Closed<span className="tab-count">{closedCount}</span></> },
          ]}
        />
        {tab === "positions" && positions.length > 0 && (
          <div className="ps-tools">
            {valuations && (
              <span className={`num ps-total ${tone(valuations.openPnl)}`} title="Open PnL after impact and fees">
                {pnl(valuations.openPnl)}
              </span>
            )}
            <Segmented<"all" | "market">
              label="Show"
              size="sm"
              value={scope}
              onChange={setScope}
              options={[
                { value: "all", label: "All" },
                { value: "market", label: instrument.symbol },
              ]}
            />
            {confirmAll ? (
              <span className="ps-confirm">
                <Button size="sm" tone="danger" onClick={() => {
                  setConfirmAll(false);
                  void close(openIds, `Close ${openIds.length}`);
                }}>
                  Close {openIds.length}
                </Button>
                <Button size="sm" tone="ghost" onClick={() => setConfirmAll(false)}>
                  Keep
                </Button>
              </span>
            ) : (
              <Button size="sm" tone="ghost" disabled={openIds.length === 0} onClick={() => setConfirmAll(true)}>
                Close all
              </Button>
            )}
          </div>
        )}
      </header>

      <div className="ps-body">
        {tab === "positions" &&
          (!account ? (
            <Empty title="No account yet" body="Connect a wallet to see your positions." />
          ) : positions.length === 0 ? (
            <Empty title="No open positions" body={`Pick a side and a leverage. Every ${instrument.symbol} trade fills at Hyperliquid's mid, no spread, no funding.`} />
          ) : (
            <>
              <div className="table-scroll ps-table">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Market</th>
                      <th>Size</th>
                      <th>Margin</th>
                      <th>Entry</th>
                      <th className="ps-col-mark">Mark</th>
                      <th>Bust</th>
                      <th>PnL</th>
                      <th>Risk</th>
                      <th className="ps-col-age">Age</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {positions.map((position) => (
                      <PositionRow
                        key={position.positionId}
                        position={position}
                        instrument={symbolOf(position)}
                        closing={closing.has(position.positionId)}
                        busy={busy.has(position.positionId)}
                        onClose={() => void close([position.positionId], `Close ${symbolOf(position)?.symbol ?? ""}`)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="ps-cards">
                {positions.map((position) => (
                  <PositionCard
                    key={position.positionId}
                    position={position}
                    instrument={symbolOf(position)}
                    closing={closing.has(position.positionId)}
                    busy={busy.has(position.positionId)}
                    onClose={() => void close([position.positionId], `Close ${symbolOf(position)?.symbol ?? ""}`)}
                  />
                ))}
              </div>
            </>
          ))}
        {tab === "pending" && <Pending operations={operations} instruments={instruments} />}
        {tab === "closed" && <Closed instruments={instruments} />}
      </div>
    </section>
  );
}
